/**
 * **Subsystem 5 — auto-pick finally has something to pick.**
 *
 * §6's auto-pick already exists in the model; the MVP simply *"never has a second transport to
 * upgrade to for residents."* This module supplies it — and **D189(a) changed what "supplies"
 * means**, because the first shape of it made the second transport a *replacement* for the first.
 *
 * **What went wrong, since the fix only makes sense against it.** The original returned one
 * transport, and online it returned BLE the moment a credential happened to be warm. Gate Detail
 * branched exclusively on that value, so a screen that painted as *cloud* re-painted seconds later
 * as *BLE-only* on the next unrelated re-render, and from then on the cloud path was not attempted
 * at all — on a device with perfectly good reception. §6 says cloud is the primary path (D4) and
 * that BLE is a bonus; a picker that can *return* BLE instead of cloud cannot express that.
 *
 * So {@link pickTransport} now returns a **pair**: a primary, and the transport a **failed**
 * primary may be retried on. The rules it serves:
 *
 *  - **Cloud is the primary while online. Always.** Not "unless BLE looks faster" — always. The
 *    old `reason: 'faster'` branch is withdrawn.
 *  - **BLE is offered beside it**, and `credentialHeld` decides whether that offer is *ready* now
 *    or would cost an `ble-access` round trip first. That is the same real fact the withdrawn
 *    branch encoded; D189 reads it for the right question.
 *  - **Never a choice the user makes** *for the happy path*. Gate Detail shows a calm line naming
 *    both, and the result chip records **the transport actually used**. What D189(b) does add is a
 *    control after a **failure**, which is a different thing from a transport selector.
 *  - **PEC-13 — lock the transport before dispatch.** One transport per attempt, and **never a
 *    silent re-send over BLE after a cloud command may have landed.**
 *
 * **The last rule is still the one this file is shaped around, and D189 does not soften it.**
 * {@link pickTransport} returns a decision and the caller dispatches; there is no callback into
 * this module once an attempt has begun, and **nothing here observes a cloud request in flight** —
 * `PickInput` is deliberately unchanged for exactly that reason, and `autoPick.test.ts` asserts its
 * key set to keep it so. The fallback is *licensed* here and *classified* at the screen, which is
 * the only place that can see whether the failed request ever reached the network. See
 * {@link CloudVerdict}.
 *
 * **Two residual risks are preserved here rather than solved** (seams §7 rule 5):
 *
 *  - **BLE presence is not provable (D86).** Range is a heuristic for *transport selection*,
 *    never evidence of who is where. Nothing below stores, reports, or timestamps a range
 *    reading, and `inRange()`'s `undefined` is treated as *unknown* rather than as either answer.
 *  - **Both transports are plaintext (D60/D85).** This adds a second plaintext path and mitigates
 *    neither; the exposure compounds, and the DPIA (D73) already records it.
 */

import type { FreshnessVerdict } from './freshness'

/**
 * What a failed cloud attempt licenses — **the whole of PEC-13, at the one point where it bites.**
 *
 * A command that never reached the network was never dispatched, so opening the gate locally is a
 * **first** attempt. A command that reached the network may have reached the gate, and a local
 * open after it is the second dispatch PEC-13 forbids — so the client **asks** instead of
 * assuming, and the resident's own tap is what authorizes the retry.
 *
 * The classification lives at the screen rather than here, because it needs the error object and
 * the elapsed time, and letting this module see either would be the in-flight visibility the
 * header refuses. The *vocabulary* lives here so both screens name the two states identically.
 */
export type CloudVerdict = 'never-dispatched' | 'ambiguous'

/**
 * The transport plan for **one attempt** — a primary, and what a failure of it may fall back to.
 *
 * **A pair rather than a choice** (D189(a)): a single-transport return is what let BLE replace
 * cloud rather than back it up. Every variant is also a different line on the screen, which is why
 * the reasons survive as strings.
 */
export type TransportPlan =
  /**
   * Online. Cloud is the primary path (D4) and needs no justification; what varies is whether
   * there is anything to fall back to.
   */
  | {
      readonly primary: 'cloud'
      readonly reason: 'default'
      /**
       * The transport a **failed** primary may be retried on — never one it "upgrades" to.
       * `null` when the platform, the grant, or D61 rules BLE out.
       */
      readonly fallback: 'ble' | null
      /**
       * Whether that fallback could run **with no round trip**.
       *
       * `false` means BLE is offerable but would need `ble-access` first — which is the very
       * fetch that has just failed if cloud failed for reachability. So this is what separates an
       * automatic fallback from one the resident has to ask for, and it is a real test rather
       * than a proxy: with a credential in hand BLE is a local exchange, without one it is a
       * round trip plus the chooser plus the exchange.
       */
      readonly fallbackReady: boolean
    }
  /**
   * Offline, inside D61's window, with BLE available. **The capability D94 said we gave up** —
   * and here BLE is the primary because there is no other, not because it won a comparison.
   */
  | {
      readonly primary: 'ble'
      readonly reason: 'offline'
      readonly fallback: null
      readonly fallbackReady: false
    }
  /**
   * Nothing can open this gate right now. Carries **why**, because the copy differs and a
   * refusal that misnames the cause sends a resident to support with the wrong problem.
   */
  | {
      readonly primary: 'none'
      readonly reason: 'offline-stale' | 'offline-never-synced' | 'offline-no-ble'
      /**
       * Present, and always empty. A refusal genuinely has no fallback, so stating it is true
       * rather than padding — and it keeps `plan.fallback` readable on every variant, so a caller
       * asking *"is there a local path?"* does not have to narrow the union first and cannot
       * accidentally answer the question only for the two variants it remembered to handle.
       */
      readonly fallback: null
      readonly fallbackReady: false
    }

export interface PickInput {
  /** Whether the cloud is reachable — `SessionState.online`, the same value that gates every
   *  other cloud action. */
  readonly online: boolean
  /** Whether this browser exposes Web Bluetooth at all. Android/Chrome in Stage 1 (§7.12). */
  readonly bleSupported: boolean
  /**
   * Whether the grant permits BLE. **This is a display input, never the authorization.** The
   * server evaluates `evaluateAccess({transport: 'ble'})` on every `ble-access` fetch, and a
   * cached grant never authorizes a command (`device-cache.md`). Getting this wrong costs a
   * wasted round trip and a refusal, not access.
   */
  readonly blePermitted: boolean
  /** D61, already evaluated — see `freshness.ts`. */
  readonly freshness: FreshnessVerdict
  /**
   * Whether a credential is already held in memory for this gate.
   *
   * **Under D189 this is readiness, not eligibility** — the same fact, asked the right question.
   * With a credential in hand BLE is a local exchange; without one it is an `ble-access` round
   * trip **plus** the chooser prompt **plus** the exchange. That never made BLE the better
   * *choice* (it is not chosen over a working cloud path any more), but it is exactly what
   * decides whether a fallback can run **automatically** or has to be offered: after a cloud
   * failure for reachability, the round trip a cold credential needs is the one that just failed.
   *
   * **R4/D217 — this is `holdsCredential()`, and it used to be `hasCredential()`.** The two are
   * `credentials.ts`'s deliberate pair: *is one held?* against *would BLE need a round trip?*.
   * Both screens passed the second, so a credential **held but past the server's five-minute
   * reuse hint** read as absent, and the automatic fallback was declined on a copy that was
   * sitting in memory — after which the offered retry re-fetched, met the network that had just
   * failed, and told the resident *there is no local access for this gate*. That is D189(d)'s own
   * defect arriving on the path where the app merely **believes** it is online, which is every
   * reachability failure and precisely what the fallback exists for.
   *
   * **The field's own name was always the held question**, and the two callers answered a
   * different one.
   *
   * **It is only true to say "no round trip" because the dispatch changed with it.** A
   * `never-dispatched` verdict now dispatches with `offline: true`, which suppresses the expiry
   * check and lets the held copy through (`withCredential`). Moving this predicate **alone**
   * would auto-dispatch and then fail inside the re-fetch — a silent failure where there used to
   * be an offer, which is worse than the defect. The two halves travel together.
   */
  readonly credentialHeld: boolean
}

/**
 * Choose one transport for **one attempt**, before dispatch (PEC-13).
 *
 * The decision is a pure function of the inputs above: no timers, no in-flight state, no memory
 * of the last attempt. A user-initiated second attempt calls this again and gets its own answer —
 * and produces **two distinct rows**, after PEC-8's uncertainty warning, which is the screen's
 * job rather than this module's.
 */
export function pickTransport(input: PickInput): TransportPlan {
  const bleUsable = input.bleSupported && input.blePermitted

  if (input.online) {
    /*
     * **Cloud, unconditionally.** There is no branch here that can return BLE while online, and
     * that absence is D189(a): the withdrawn `reason: 'faster'` upgrade is what made a screen
     * stop attempting the primary path on a device with working reception.
     *
     * D61 is checked for the *fallback*, even though the device is online and could re-sync in a
     * moment. A credential held past the window is not honoured, full stop; making the window
     * depend on connectivity would give it two meanings.
     */
    const fallbackUsable = bleUsable && input.freshness.ok
    return {
      primary: 'cloud',
      reason: 'default',
      fallback: fallbackUsable ? 'ble' : null,
      fallbackReady: fallbackUsable && input.credentialHeld,
    }
  }

  /*
   * Offline. **This is the capability D94 named as the cost of deferring this PRD** — *"a
   * cellular outage at the gate means no resident can open it at all"* — so the branch below is
   * the one the flip condition was about.
   */
  const refused = { fallback: null, fallbackReady: false } as const
  if (!bleUsable) return { primary: 'none', reason: 'offline-no-ble', ...refused }
  if (!input.freshness.ok) {
    return {
      primary: 'none',
      reason: input.freshness.reason === 'stale' ? 'offline-stale' : 'offline-never-synced',
      ...refused,
    }
  }
  /*
   * **A credential is not required here, and that asymmetry is deliberate.** Online, holding one
   * decides whether a fallback can run without a round trip. Offline there is no round trip to
   * avoid: if a credential is in memory the open succeeds — **including past the server's
   * five-minute reuse hint, which D189(d) confined to the online path where it belongs** — and if
   * there is none, `withCredential`'s offline branch refuses at once and the screen says so.
   * Refusing in advance would name an implementation detail ("no credential") rather than
   * anything the resident can act on.
   */
  return { primary: 'ble', reason: 'offline', fallback: null, fallbackReady: false }
}

/**
 * The calm line §6 specifies, restated for a **pair** — *"Opens via Cloud · Bluetooth as backup"*.
 *
 * `'transportBleFaster'` is gone with the branch that produced it. It had never existed in either
 * catalog and the screen filtered it out by name, which is what a key with no meaning looks like
 * from the far end.
 *
 * Returned as an i18n key rather than a string: the shell is RTL Hebrew first, and a transport
 * label baked in here would be the one line on Gate Detail that could not be translated.
 * `null` means **say nothing**, which is the right answer whenever the choice is the ordinary
 * one on a device that has no second transport to mention.
 */
export function transportLineKey(plan: TransportPlan, bleSupported: boolean): string | null {
  if (plan.primary === 'ble') return 'transportBleOffline'
  if (plan.primary === 'none') return `refusal.${plan.reason}`
  // **Naming both is the point** (D189(a)): the line the resident reads should say that cloud
  // carries the command and Bluetooth is behind it, which is the sentence the old shape could not
  // produce because it only ever had one transport to name.
  if (plan.fallback === 'ble') return 'transportCloudBleBackup'
  // On a device with no BLE at all there is no second transport, so naming the first one is
  // noise — the same reason P7's existing screen shows one glyph and no explanation today.
  return bleSupported ? 'transportCloud' : null
}
